import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import BusinessCard from "@/components/BusinessCard";
import { Pin, ExternalLink, Sparkles } from "lucide-react";

export default function SharedBoard() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Discourage search-engine indexing of shared boards
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    api.get(`/boards/shared/${token}`).then(r => setData(r.data)).catch(() => setNotFound(true));
    return () => { try { document.head.removeChild(meta); } catch (_) { /* meta already removed */ } };
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F2F4F2] grid place-items-center px-6">
        <div className="text-center">
          <Pin size={32} className="mx-auto text-[#C79B6B] mb-3"/>
          <h1 className="text-2xl font-bold mb-1" style={{fontFamily:'Outfit'}}>Board not found</h1>
          <p className="text-sm text-[#222]/60 mb-5">This share link has been revoked or doesn&apos;t exist.</p>
          <button onClick={() => navigate("/")} className="btn-lime px-5 py-2 text-sm">Visit LIMYA</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center text-[#222]/50">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F2F4F2]">
      {/* Slim public header — no logged-in state */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-black/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center">
            <img src="/logo-full.png" alt="LIMYA" className="h-8 w-auto"/>
          </Link>
          <Link to="/register" className="text-xs btn-lime px-4 py-2 flex items-center gap-1.5" data-testid="shared-join-btn">
            <Sparkles size={12}/> Build your own board
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <p className="handwritten text-2xl text-[#C79B6B] mb-1">{data.owner_first_name}&apos;s</p>
          <h1 className="text-4xl sm:text-5xl font-bold" style={{fontFamily:'Outfit'}} data-testid="shared-title">Neighborhood Board</h1>
          <p className="text-sm text-[#222]/60 mt-3" data-testid="shared-count">
            {data.pro_count} pro{data.pro_count === 1 ? "" : "s"} pinned · view-only
          </p>
        </div>

        {data.sections.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
            <p className="text-sm text-[#222]/60">This board doesn&apos;t have any pros to show right now.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {data.sections.map(section => (
              <section key={section.name} data-testid={`shared-section-${section.name.replace(/\s+/g,'-')}`}>
                <h2 className="text-xs uppercase tracking-widest text-[#222]/50 mb-4 font-bold">{section.name}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {section.pros.map((p, i) => (
                    <div key={p.user_id} className="relative" data-testid={`shared-card-${p.user_id}`}>
                      <BusinessCard pro={p} index={i} variant="grid"/>
                      <a
                        href={`/pro/${p.user_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white border border-black/10 shadow-sm hover:bg-black/5"
                        title="View profile"
                        data-testid={`shared-view-${p.user_id}`}
                      ><ExternalLink size={12}/></a>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <p className="text-[11px] text-[#222]/40 italic text-center mt-12">
          A view-only board on Limya. Cards link to public pro profiles.
        </p>
      </div>
    </div>
  );
}
